import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
} from '@mui/material';
import type { FixedDynamicLineItem } from '@vy/dynamic-invoice-builder';
import { VyIcon } from '@vy/ui-design-system';
import { formatPrice } from '@vy/services';
import type { Currency } from '@vy/types';
import { isNotNullOrUndefined } from '@vy/utils';
import { FC } from 'react';
import { dynamicInvoiceRevisionLineItemSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { useMenu } from '../../../../../hooks/use-menu';
import { useDynamicInvoiceIds } from '../../hooks/use-dynamic-invoice-ids';
import { getLineItemPercentage } from '../utils/get-line-item-percentage';
import { DynamicInvoiceDisableUsageLineItem } from './DynamicInvoiceDisableUsageLineItem';
import { LineItemHeader } from './LineItemHeader';
import { MetricBox } from './MetricBox';
import { lineItemActionStyles, usageItemAccordionStyles } from './styles';

type RevisionedFeeItemProps = {
  lineItem: FixedDynamicLineItem;
  currency: Currency;
  onDelete: (lineItemId: string) => void;
};

export const RevisionedFeeItem: FC<RevisionedFeeItemProps> = ({ lineItem, currency, onDelete }) => {
  const { customerId, reviewStatus } = useDynamicInvoiceIds();
  const { anchorElement, isOpen, handleMenuOpen, handleMenuClose } = useMenu();

  const shouldShowMenu = reviewStatus !== 'Approved';
  const isDisabled = lineItem.disabled;

  const percentage = getLineItemPercentage(lineItem);

  const handleDelete = () => {
    handleMenuClose();
    onDelete(lineItem.lineItemId);
  };

  return (
    <Box
      sx={{
        ...usageItemAccordionStyles.accordion,
        padding: '16px 24px',
        opacity: isDisabled ? 0.5 : 1,
      }}
      {...getSelectorAttribute(dynamicInvoiceRevisionLineItemSelectors.revisionLineItem)}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%"
        }}>
        <LineItemHeader
          name={lineItem.name}
          description={lineItem.description}
          percentage={percentage}
        />

        <Stack
          direction="row"
          sx={[{
            alignItems: "center"
          }, lineItemActionStyles.actionsContainer]}>
          <Stack
            direction="row"
            sx={[{
              alignItems: "center"
            }, lineItemActionStyles.metricsContainer]}>
            {isNotNullOrUndefined(lineItem.basePrice) && (
              <Box {...getSelectorAttribute(dynamicInvoiceRevisionLineItemSelectors.revisionBasePrice)}>
                <MetricBox label="Base amount" value={formatPrice(lineItem.basePrice, currency)} />
              </Box>
            )}
            <Box {...getSelectorAttribute(dynamicInvoiceRevisionLineItemSelectors.revisionPrice)}>
              <MetricBox label="Amount" value={formatPrice(lineItem.totalAmount, currency)} />
            </Box>
          </Stack>
          {shouldShowMenu && (
            <div>
              <IconButton
                size="medium"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuOpen(e);
                }}
              >
                <VyIcon icon="DotsMenu" />
              </IconButton>

              <Menu
                anchorEl={anchorElement}
                open={isOpen}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                onClick={(e) => e.stopPropagation()}
              >
                <MenuItem onClick={handleDelete}>
                  <ListItemIcon>
                    <VyIcon icon="Delete" style={{ transform: 'scale(0.8)' }} />
                  </ListItemIcon>
                  <ListItemText>Delete Item</ListItemText>
                </MenuItem>

                <DynamicInvoiceDisableUsageLineItem
                  lineItemId={lineItem.lineItemId}
                  disabled={isDisabled}
                  closeMenu={handleMenuClose}
                  customerId={customerId}
                />
              </Menu>
            </div>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};
